import { formatDate } from "@angular/common";
import { Component, Input, OnInit } from "@angular/core";
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { BaseResizeComponent } from "loi-lti-common";
import { forkJoin, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { LtiRequest } from '../models/lti-response.model';
import { ModuleLineItem, ModulePartsModel } from "../models/module-parts.model";
import { MeetingSeriesDetails, SeriesMeetingModel } from "../models/seriesmeeting.model";
import { ContractType, ServiceContractModel } from "../models/student.curriculum.model";
import { LtiRequestService } from '../services/lti-request.service';
import { ModuleService } from "../services/module.service";
import { SeriesMeetingService } from "../services/seriesmeeting.service";


@Component({
  selector: 'module-parts-component',
  templateUrl: './module.parts.component.html',
  styleUrls: ['./module.parts.component.scss']
})

export class ModulePartsComponent extends BaseResizeComponent implements OnInit {

  private ltiRequest: LtiRequest;
  @Input('moduleId') moduleId: string;
  @Input('contractType') contractType: ContractType;
  isStudent: boolean = true;
  remote: boolean = true;
  serviceContract: ServiceContractModel;
  parts: ModuleLineItem[];
  meetingSeries: MeetingSeriesDetails[];
  meetingSeriesMandatory: MeetingSeriesDetails[];
  meetingSeriesExtra: MeetingSeriesDetails[];
  numberDescriptions: object;
  partCounter: number;
  minimalOptionsToPass: number;
  numberOfMeetingParts: number = 0;
  numberOfAssignmentsExamsParts: number;
  optionsText: string;

  constructor(
    private ltiRequestService: LtiRequestService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private translateService: TranslateService,
    private moduleService: ModuleService,
    private meetingService: SeriesMeetingService
  ) {
    super();
    this.matIconRegistry.addSvgIcon('weighing', this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/weighing.svg'));
  }

  ngOnInit() {
    this.ltiRequestService.getLtiRequest().subscribe(ltiRequest => {
      this.ltiRequest = ltiRequest.ltiRequest;
      this.isStudent = this.isStudentRole(this.ltiRequest.roles);
      this.remote = (this.contractType == ContractType.AFSTAND);
      if (this.isStudent) {
        forkJoin([this.getMeetings(), this.getModuleParts()]).subscribe(() => {
          this.processInitData();
        });
      }
      else {
        this.getModuleParts().subscribe(() => {
          this.processInitData();
        });
      }
      super.ngOnInit();
    });
  }

  async processInitData() {
    this.numberDescriptions = {
      'ONE': await this.translateService.get('Number_' + 'ONE').toPromise(),
      'TWO': await this.translateService.get('Number_' + 'TWO').toPromise(),
      'THREE': await this.translateService.get('Number_' + 'THREE').toPromise(),
    }
    this.optionsText = this.format(await this.translateService.get('OptionsText').toPromise(), this.numberToText(this.minimalOptionsToPass));
  }

  getMeetings(): Observable<MeetingSeriesDetails[]> {
    return this.meetingService.getMeetings(this.moduleId).pipe(
      map(series => {
        this.meetingSeries = series;
        this.refreshMeetings();
        return series;
      })
    )
  }

  getModuleParts(): Observable<ModulePartsModel> {
    return this.moduleService.getModuleParts(this.moduleId).pipe(
      map(moduleParts => {
        var mandatoryParts = moduleParts.moduleLineItems.filter(this.isMandatoryPart);
        this.parts = mandatoryParts.filter( p => p.moduleLineItemType == "EXERCISE");
        this.parts = this.parts.concat( mandatoryParts.filter( p => p.moduleLineItemType == "EXAM"));
        this.parts = this.parts.concat( mandatoryParts.filter( p => p.moduleLineItemType != "EXAM" && p.moduleLineItemType != "EXERCISE"));
        this.minimalOptionsToPass = moduleParts.moduleConditions.condMarksSufficient;
        this.numberOfAssignmentsExamsParts = this.minimalOptionsToPass >= 0 ? 1 : this.parts.length;
        return moduleParts;
      })
    )
  }

  isStudentRole(role: string) {
    return ['LEARNER', 'MEMBER', 'NONCREDITLEARNER'].indexOf(role.toUpperCase()) != -1;
  }

  isMandatoryMeeting(meeting: SeriesMeetingModel) {
    if (this.remote) {
      return meeting.remoteMandatory;
    }
    else {
      return meeting.localMandatory;
    }
  }

  isExam(item) {
    if (item.moduleLineItemType == "EXAM") return item;
  }

  isExercise(item) {
    if (item.moduleLineItemType == "EXERCISE") return item;
  }

  isMandatoryPart(item) {
    if (item.mandatory) return item;
  }

  loiFormattedDate(date: string): string {
    return formatDate(date, 'dd-MM-yyyy', this.translateService.defaultLang);
  }

  refreshMeetings() {
    this.meetingSeriesMandatory = [];
    for (let meetingSeries of this.meetingSeries) {
      let meetings = meetingSeries.meetings.filter(m => this.isMandatoryMeeting(m) == true);
      if (meetings.length > 0) {
        let meetingSeriesCopy = new MeetingSeriesDetails(meetingSeries);
        meetingSeriesCopy.meetings = meetings;
        this.meetingSeriesMandatory.push(meetingSeriesCopy);
      }
    };

    this.meetingSeriesExtra = [];
    for (let meetingSeries of this.meetingSeries) {
      let meetings = meetingSeries.meetings.filter(m => this.isMandatoryMeeting(m) == false);
      if (meetings.length > 0) {
        let meetingSeriesCopy = new MeetingSeriesDetails(meetingSeries);
        meetingSeriesCopy.meetings = meetings;
        this.meetingSeriesExtra.push(meetingSeriesCopy);
      }
    };

    this.numberOfMeetingParts = this.meetingSeriesMandatory.length;
  }

  mandatoryPartsExist() {
    return this.itemsExist(this.meetingSeriesMandatory) || this.itemsExist(this.parts);
  }

  extraPartsExist() {
    return this.itemsExist(this.meetingSeriesExtra);
  }

  itemsExist(list) {
    return list && list.length > 0;
  }

  format(mask, value) {
    return mask.replace("{0}", value);
  }

  numberToText(numberValue: number) {
    if (numberValue == 1) return this.numberDescriptions["ONE"];
    if (numberValue == 2) return this.numberDescriptions["TWO"];
    if (numberValue == 3) return this.numberDescriptions["THREE"];
    return numberValue.toString();
  }

}
